// @ts-nocheck
/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { X, Printer, TrendingUp, TrendingDown, Briefcase, Stethoscope, Bot, Info } from 'lucide-react';
import useAppStore, { useCalculationsSelector } from '../store/appStore';
import { evaluateRentabilitaet } from '../store/utils'; // 💉 CHIRURGIE: Doppel-Schloss Import
import { isSoloModeActive } from '../utils/soloMode';

// ============================================================================
// STYLES: EXECUTIVE CARBON EDITION (NOBLE SLATE v11.0)
// ============================================================================
const DashboardStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono-numbers { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        
        .bg-deep-space { background-color: #030303; }
        html:not(.dark) .bg-deep-space { background-color: var(--pe-bg-primary); }
        html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
        html:not(.dark) .carbon-panel { background-color: var(--pe-bg-card); border-color: var(--pe-border-subtle); }
        html:not(.dark) .carbon-panel::after { background: none; }
        .text-phantom-gold { color: #E6C299; }
        
        .carbon-weave-bg {
            background-color: #111;
            background-image: 
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        .carbon-panel {
            position: relative;
            border-radius: 1.5rem;
            border: 1px solid #2a2a2a;
            box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.05);
        }

        .carbon-panel::after {
            content: ''; position: absolute; inset: 0; background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.6)); border-radius: inherit; pointer-events: none;
        }

        .carbon-content { position: relative; z-index: 10; }

        /* NEON LED COLORS */
        .led-purple  { color: #c084fc; text-shadow: 0 0 10px rgba(192,132,252,0.6), 0 0 20px rgba(192,132,252,0.3); }
        .led-emerald { color: #10b981; text-shadow: 0 0 10px rgba(16,185,129,0.6), 0 0 20px rgba(16,185,129,0.3); }
        .led-lime    { color: #a3e635; text-shadow: 0 0 10px rgba(163,230,53,0.6),  0 0 20px rgba(163,230,53,0.3); }
        .led-amber   { color: #fbbf24; text-shadow: 0 0 10px rgba(251,191,36,0.6),  0 0 20px rgba(251,191,36,0.3); }
        .led-orange  { color: #fb923c; text-shadow: 0 0 10px rgba(251,146,60,0.6),  0 0 20px rgba(251,146,60,0.3); }
        .led-red     { color: #ef4444; text-shadow: 0 0 10px rgba(239,68,68,0.6),   0 0 20px rgba(239,68,68,0.3); }
        .led-gold    { color: #E6C299; text-shadow: 0 0 10px rgba(230,194,153,0.6), 0 0 20px rgba(230,194,153,0.3); }
        .led-white   { color: #fff; text-shadow: 0 0 15px rgba(255,255,255,0.5); }

        .metal-badge {
            background: linear-gradient(to bottom, #222, #0a0a0a);
            border: 1px solid #333;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 10px rgba(0,0,0,0.8);
            border-radius: 6px;
        }

        /* ===== LIGHT-MODE OVERRIDES (Warm-Platin) — Dark bleibt via html.dark unangetastet ===== */
        html:not(.dark) .carbon-weave-bg {
            background-color: var(--pe-bg-elevated);
            background-image: none;
        }
        html:not(.dark) .carbon-panel {
            border-color: var(--pe-border-subtle);
            box-shadow: 0 20px 45px -20px rgba(28, 25, 23, 0.18), 0 2px 8px rgba(28, 25, 23, 0.06);
        }
        html:not(.dark) .carbon-panel::after { background: none; }
        html:not(.dark) .led-purple  { color: #6D28D9; text-shadow: none; }
        html:not(.dark) .led-emerald { color: #047857; text-shadow: none; }
        html:not(.dark) .led-lime    { color: #4D7C0F; text-shadow: none; }
        html:not(.dark) .led-amber   { color: #B45309; text-shadow: none; }
        html:not(.dark) .led-orange  { color: #C2410C; text-shadow: none; }
        html:not(.dark) .led-red     { color: #B91C1C; text-shadow: none; }
        html:not(.dark) .led-gold    { color: var(--pe-accent-text); text-shadow: none; }
        html:not(.dark) .led-white   { color: #1C1917; text-shadow: none; }
        html:not(.dark) .metal-badge {
            background: linear-gradient(to bottom, #FAF9F7, #EDEAE6);
            border: 1px solid #D6D3D1;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), 0 2px 6px rgba(28,25,23,0.06);
        }
        html:not(.dark) .text-phantom-gold { color: var(--pe-accent-text); text-shadow: none; }
    `}</style>
);

// ==========================================
// TOOLTIP KOMPONENTE (GOLD / SCHWARZ)
// ==========================================
const CustomTooltip = ({ children, text, customClass = "" }: { children: React.ReactNode, text: string, customClass?: string }) => (
    <div className={`group relative flex flex-col w-full h-full cursor-help ${customClass}`}>
        {children}
        <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:-translate-y-1 z-[9999] print:hidden">
            <div className="bg-[#E6C299] text-black text-xs font-bold uppercase tracking-widest p-3 rounded-lg shadow-[0_10px_25px_-5px_rgba(0,0,0,0.5)] border border-[#c4a077] leading-relaxed text-center">
                {text}
            </div>
            <div className="w-3 h-3 bg-[#E6C299] border-b border-r border-[#c4a077] transform rotate-45 absolute -bottom-1.5 left-1/2 -translate-x-1/2"></div>
        </div>
    </div>
);

const safeDiv = (numerator: number, denominator: number): number => {
    const num = Number(numerator);
    const den = Number(denominator);
    if (!den || isNaN(num) || isNaN(den)) return 0;
    return num / den;
};

const formatCurrency = (value: number, currency = 'EUR', decimals = 0): string => {
    if (isNaN(Number(value))) return `0 ${currency}`;
    return new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value || 0) + ` ${currency}`;
};

const formatPercent = (value: number, decimals = 1): string => {
    if (isNaN(Number(value))) return '0%';
    return (Number(value) * 100).toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }) + '%';
};

const formatNumber = (value: number, decimals = 1): string => {
    if (isNaN(Number(value))) return '0';
    return Number(value).toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

export interface NormalizedSalonMonthData {
    monatName: string;
    index: number; 
    bedienumsatzBrutto: number;
    vkUmsatzBrutto: number;
    gesamtBrutto: number;
    gesamtNetto: number;
    mindestNetto: number; 
    kostendeckungNetto: number; 
    privatentnahme: number;
    isLocked: boolean;
    urlaub: number;
    krank: number;
}

interface ExecutiveSummaryModalGesamtProps {
    isOpen: boolean;
    onClose: () => void;
    title: string; 
    currencyCode: string;
    mwstFaktor: number;
    auslastungsVorgabe?: number; 
    ruecklagenZielFaktor: number;
    stammdaten: {
        monatsStunden: number;
        durchschnittlicherUmsatzProTag: number;
        /** Salon-Arbeitstage pro Monat (Öffnungstage × Wochensatz) */
        arbeitstageProMonat?: number;
        /** Produktiv-Köpfe inkl. Unternehmer — für Team-Ausfallquote */
        teamSize?: number;
    };
    rawMonthsData: NormalizedSalonMonthData[];
    isHJ2Freigegeben?: boolean; 
    rentabilitaetIst: number; 
    teamEffizienzIst: number;
    salonStundensatz?: number;
}

const getEvaluationLevel = (value: number, type: 'effizienz' | 'auslastung' | 'vk') => {
    let level = 6;
    if (type === 'auslastung') {
        if (value >= 0.85) level = 1;
        else if (value >= 0.75) level = 2;
        else if (value >= 0.70) level = 3;
        else if (value >= 0.60) level = 4;
        else if (value >= 0.50) level = 5;
    } else if (type === 'effizienz') {
        if (value >= 1.00) level = 1;
        else if (value >= 0.95) level = 2;
        else if (value >= 0.89) level = 3;
        else if (value >= 0.72) level = 4;
        else if (value >= 0.63) level = 5;
    } else if (type === 'vk') {
        if (value >= 0.15) level = 1;
        else if (value >= 0.10) level = 2;
        else if (value >= 0.07) level = 3;
        else if (value >= 0.05) level = 4;
        else if (value >= 0.03) level = 5;
    }

    switch (level) {
        case 1: return { label: 'Exzellent', textClass: 'text-purple-700 dark:text-purple-400 led-purple print:text-purple-700', borderClass: 'border-purple-500/50 print:border-purple-300', bgClass: 'bg-pe-card dark:bg-[#1c1c1e] print:bg-purple-50', badgeBg: 'bg-pe-card dark:bg-black border border-purple-500/50', shadowClass: 'print:drop-shadow-none' };
        case 2: return { label: 'Sehr Gut', textClass: 'text-emerald-700 dark:text-emerald-400 led-emerald print:text-emerald-700', borderClass: 'border-emerald-500/50 print:border-emerald-300', bgClass: 'bg-pe-card dark:bg-[#1c1c1e] print:bg-emerald-50', badgeBg: 'bg-pe-card dark:bg-black border border-emerald-500/50', shadowClass: '' };
        case 3: return { label: 'Gut', textClass: 'text-lime-700 dark:text-lime-400 led-lime print:text-lime-700', borderClass: 'border-lime-500/50 print:border-lime-300', bgClass: 'bg-pe-card dark:bg-[#1c1c1e] print:bg-lime-50', badgeBg: 'bg-pe-card dark:bg-black border border-lime-500/50', shadowClass: '' };
        case 4: return { label: 'Akzeptabel', textClass: 'text-amber-700 dark:text-amber-400 led-amber print:text-amber-700', borderClass: 'border-amber-500/50 print:border-amber-300', bgClass: 'bg-pe-card dark:bg-[#1c1c1e] print:bg-amber-50', badgeBg: 'bg-pe-card dark:bg-black border border-amber-500/50', shadowClass: '' };
        case 5: return { label: 'Kritisch', textClass: 'text-orange-700 dark:text-orange-400 led-orange print:text-orange-700', borderClass: 'border-orange-500/50 print:border-orange-300', bgClass: 'bg-pe-card dark:bg-[#1c1c1e] print:bg-orange-50', badgeBg: 'bg-pe-card dark:bg-black border border-orange-500/50', shadowClass: '' };
        case 6: default: return { label: 'Defizitär', textClass: 'text-red-700 dark:text-red-400 led-red print:text-red-700', borderClass: 'border-red-500/50 print:border-red-300', bgClass: 'bg-pe-card dark:bg-[#1c1c1e] print:bg-red-50', badgeBg: 'bg-pe-card dark:bg-black border border-red-500/50', shadowClass: '' };
    }
};

// 💉 CHIRURGIE: getRentabilitaetStatus via Doppel-Schloss (utils.ts)
const getRentabilitaetStatus = (gehalt: number, margin: number) => {
    const evalResult = evaluateRentabilitaet(gehalt, margin);
    
    let fallbackText = "";
    switch (evalResult.status) {
        case 'Exzellent': 
            fallbackText = "Hervorragende Rentabilität. Das absolute Gehalt und der Überschuss sind zukunftssicher.";
            return { color: 'text-purple-700 dark:text-purple-400 led-purple print:text-purple-700 print:drop-shadow-none', border: 'border-purple-500/50 print:border-purple-300', label: 'EXZELLENT', text: evalResult.text || fallbackText };
        case 'Gut': 
            fallbackText = "Gute Rentabilität. Das Gehalt und der Überschuss bilden ein gesundes Fundament.";
            return { color: 'text-emerald-700 dark:text-emerald-400 led-emerald print:text-emerald-700', border: 'border-emerald-500/30 print:border-emerald-300', label: 'GUT', text: evalResult.text || fallbackText };
        case 'Solide': 
            fallbackText = "Solide Rentabilität. Das Geschäft trägt sich gut, bietet aber noch Optimierungspotenzial.";
            return { color: 'text-lime-700 dark:text-lime-400 led-lime print:text-lime-600', border: 'border-lime-500/30 print:border-lime-300', label: 'SOLIDE', text: evalResult.text || fallbackText };
        case 'Grenzwertig': 
            fallbackText = "Grenzwertige Rentabilität. Der Überschuss oder das Gehalt ist zu niedrig für langfristige Sicherheit.";
            return { color: 'text-amber-700 dark:text-amber-400 led-amber print:text-amber-600', border: 'border-amber-500/30 print:border-amber-300', label: 'GRENZWERTIG', text: evalResult.text || fallbackText };
        case 'Kritisch': 
        default: 
            fallbackText = "Veto-Auslösung: Kritischer Zustand. Weder das absolute Gehalt noch der Überschuss sind tragfähig.";
            return { color: 'text-red-700 dark:text-red-400 led-red print:text-red-700', border: 'border-red-500/30 print:border-red-300', label: evalResult.status === 'Kritisch' ? 'KRITISCH' : 'KRITISCH BIS INSTABIL', text: evalResult.text || fallbackText };
    }
};

const OriginalLeistungsFeedbackBox: React.FC<{
    ruecklagenZielFaktor: number;
    istRuecklagenPct: number;
    gewinn: number;
    hasData: boolean;
}> = ({ ruecklagenZielFaktor, istRuecklagenPct, gewinn, hasData }) => {
    const zielProzentLabel = formatPercent(ruecklagenZielFaktor || 0, 0);
    const istProzentLabel = formatPercent(istRuecklagenPct || 0, 1);

    let boxClasses = "bg-pe-card dark:bg-[#1c1c1e] border-pe-border-strong";
    let iconClass = "text-slate-500";
    let titleClass = "text-pe-muted dark:text-slate-400";
    let bodyClass = "text-pe-muted";
    let title = "Datenerfassung";
    let text: React.ReactNode = "Trage deine monatlichen Umsätze ein, um eine Auswertung zu erhalten.";

    if (!hasData) {
        // defaults above
    } else if (gewinn < 0) {
        boxClasses = "bg-red-100/80 dark:bg-red-950/20 border-red-300 dark:border-red-500/50";
        iconClass = "text-red-500 led-red";
        titleClass = "text-red-950 dark:text-red-400";
        bodyClass = "text-red-900 dark:text-red-300";
        title = "SYSTEM-ALARM: KRITISCHE SITUATION!";
        text = <>Der Verlust ist so hoch, dass der kalkulatorische Unternehmerlohn nicht mehr gedeckt ist. IST-Rücklage <strong className="text-pe-text font-black font-mono-numbers">{istProzentLabel}</strong> (Ziel {zielProzentLabel}).</>;
    } else if (istRuecklagenPct < 0) {
        boxClasses = "bg-orange-100/80 dark:bg-orange-950/20 border-orange-300 dark:border-orange-500/50";
        iconClass = "text-orange-500 led-orange";
        titleClass = "text-orange-950 dark:text-orange-400";
        bodyClass = "text-orange-900 dark:text-orange-300";
        title = "Dringender Handlungsbedarf";
        text = <>Das Unternehmen arbeitet derzeit nicht kostendeckend. IST-Rücklage <strong className="text-pe-text font-black font-mono-numbers">{istProzentLabel}</strong> (Ziel {zielProzentLabel}).</>;
    } else if (istRuecklagenPct < ruecklagenZielFaktor) {
        boxClasses = "bg-amber-100/80 dark:bg-amber-950/20 border-amber-300 dark:border-amber-500/50";
        iconClass = "text-amber-700 dark:text-amber-400 led-amber";
        titleClass = "text-amber-950 dark:text-amber-400";
        bodyClass = "text-pe-muted";
        title = "Ziel nicht erreicht, aber kostendeckend";
        text = <>Das Unternehmen ist kostendeckend. IST-Rücklage <strong className="text-pe-text font-black font-mono-numbers">{istProzentLabel}</strong>, das Rücklagenziel von <strong className="text-pe-text font-black font-mono-numbers">{zielProzentLabel}</strong> wurde jedoch noch nicht erreicht.</>;
    } else {
        boxClasses = "bg-emerald-100/80 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-500/30";
        iconClass = "text-emerald-500 led-emerald";
        titleClass = "text-emerald-950 dark:text-emerald-400";
        bodyClass = "text-emerald-900 dark:text-emerald-300";
        title = "Hervorragende Leistung!";
        text = <>Glückwunsch! IST-Rücklage <strong className="text-pe-text font-black font-mono-numbers">{istProzentLabel}</strong> — das Rücklagenziel von <strong className="text-pe-text font-black font-mono-numbers">{zielProzentLabel}</strong> wurde übertroffen.</>;
    }

    return (
        <div className={`p-5 rounded-xl flex items-start space-x-4 h-full border shadow-xl print:bg-white print:border-slate-300 print:text-black font-sans ${boxClasses}`}>
            <div className="flex-shrink-0 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-10 w-10 ${iconClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <div>
                <h3 className={`font-bold text-sm uppercase tracking-widest leading-tight ${titleClass}`}>{title}</h3>
                <div className={`text-sm mt-1.5 leading-normal ${bodyClass}`}>
                    {text}
                </div>
            </div>
        </div>
    );
};

const KICard = ({ type, title, children, icon }: { type: 'info' | 'warning' | 'success' | 'excellent' | 'critical', title: string, children: React.ReactNode, icon: string }) => {
    let bgClass = 'bg-pe-card dark:bg-black border-pe-border-strong text-pe-text dark:text-slate-100 shadow-inner'; 
    let titleClass = 'text-amber-700 dark:text-amber-400';

    if (type === 'success') {
        titleClass = 'text-emerald-700 dark:text-emerald-400';
    } else if (type === 'excellent') {
        titleClass = 'text-phantom-gold';
    } else if (type === 'critical') {
        titleClass = 'text-red-700 dark:text-red-400';
    }

    return (
        <div className={`p-5 rounded-xl border ${bgClass} print:bg-white print:border-slate-300 print:text-black`}>
            <div className="flex items-start gap-4">
                <span className="text-xl mt-0.5 drop-shadow-md">{icon}</span>
                <div>
                    <h4 className={`font-black text-xs uppercase tracking-widest mb-2 print:text-black ${titleClass}`}>{title}</h4>
                    <div className="text-sm text-pe-muted dark:text-slate-300 leading-relaxed">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ExecutiveSummaryModalGesamt: React.FC<ExecutiveSummaryModalGesamtProps> = ({
    isOpen,
    onClose,
    title,
    currencyCode,
    mwstFaktor,
    auslastungsVorgabe = 75,
    ruecklagenZielFaktor,
    stammdaten,
    rawMonthsData,
    isHJ2Freigegeben = false,
    teamEffizienzIst,
    salonStundensatz = 0
}) => {
    const [mounted, setMounted] = useState(false);
    const [startMonth, setStartMonth] = useState<number>(0);
    const [endMonth, setEndMonth] = useState<number>(5); 
    const [vereinbarungText, setVereinbarungText] = useState('');
    const [showAiAnalysis, setShowAiAnalysis] = useState(false);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    const [aiDiagnostics, setAiDiagnostics] = useState<any[]>([]); 

    // 💉 CHIRURGIE: STRIKT 1:1 Variablen-Sync mit GesamtleistungPage
    const calculationsStore = useCalculationsSelector();
    const storeUeberschussProzent = calculationsStore?.ueberschussProzent || 0;
    const storeRentabilitaetIst = calculationsStore?.rentabilitaetIst || 0;
    const inhaberErtragIst = calculationsStore?.inhaberErtragIst || 0;
    
    const store = useAppStore();
    const clientData = store.clientData || {};
    
    const unternehmerData = useAppStore(state => state.clientData?.unternehmerData);
    const gesamtUnternehmerlohn = (unternehmerData?.entnahme || 0) + (unternehmerData?.sozialkosten || 0);

    const isSoloActive = isSoloModeActive(clientData);
    const isBaseActive = clientData.basicChecked === true || clientData.lizenzDetails?.typ === 'basic' || clientData.lizenzDetails?.typ === 'base';
    
    const showTeamData = !isSoloActive && !isBaseActive;

    const monateNamen = useMemo(() => [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ], []);

    const maxSelectableMonth = isHJ2Freigegeben ? 11 : 5;

    const validMonths = useMemo(() => {
        const valid = rawMonthsData
            .filter(m => (m.gesamtBrutto > 0 || m.isLocked) && m.index <= maxSelectableMonth)
            .map(m => m.index);
        return valid.length > 0 ? valid : [0]; 
    }, [rawMonthsData, maxSelectableMonth]);

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        if (isOpen && validMonths.length > 0) {
            setStartMonth(prev => validMonths.includes(prev) ? prev : validMonths[0]);
            setEndMonth(prev => validMonths.includes(prev) ? prev : validMonths[validMonths.length - 1]);
            setShowAiAnalysis(false);
            setAiError(null);
            setAiDiagnostics([]);
        }
    }, [isOpen, validMonths]); 

    const analysis = useMemo(() => {
        const activeMonths = rawMonthsData.filter(
            m => m.index >= startMonth && m.index <= endMonth && (m.gesamtBrutto > 0 || m.isLocked)
        );
        
        const count = activeMonths.length || 1;
        const safeRuecklagenFaktor = Number(ruecklagenZielFaktor) || 0;

        let totalBedienBrutto = 0; let totalVkBrutto = 0; let totalGesamtBrutto = 0;
        let totalGesamtNetto = 0; let totalSollNetto = 0; let totalPrivatentnahme = 0;
        let totalUrlaub = 0; let totalKrank = 0;
        let totalKostenNetto = 0;

        activeMonths.forEach(m => {
            totalBedienBrutto += m.bedienumsatzBrutto || 0;
            totalVkBrutto += m.vkUmsatzBrutto || 0;
            totalGesamtBrutto += m.gesamtBrutto || 0;
            totalGesamtNetto += m.gesamtNetto || 0;
            totalSollNetto += m.mindestNetto || 0;
            totalPrivatentnahme += m.privatentnahme || 0;
            totalUrlaub += m.urlaub || 0;
            totalKrank += m.krank || 0;
            totalKostenNetto += m.kostendeckungNetto || 0;
        });

        const avgBedienBrutto = totalBedienBrutto / count;
        const avgVkBrutto = totalVkBrutto / count;
        const avgGesamtBrutto = totalGesamtBrutto / count;
        const avgGesamtNetto = totalGesamtNetto / count;
        const avgSollNetto = totalSollNetto / count; 
        const avgPrivatentnahme = totalPrivatentnahme / count;
        const avgKostenNetto = totalKostenNetto / count;
        
        const avgZielNetto = avgSollNetto * (1 + safeRuecklagenFaktor);

        const stdUmsatzIstBrutto = safeDiv(avgGesamtBrutto, stammdaten.monatsStunden);
        const stdUmsatzSollBrutto = safeDiv(avgZielNetto * mwstFaktor, stammdaten.monatsStunden); 
        const stdUmsatzSollBei75 = safeDiv(stdUmsatzSollBrutto, auslastungsVorgabe / 100);
        const umsatzauslastung = (auslastungsVorgabe / 100) * safeDiv(stdUmsatzIstBrutto, stdUmsatzSollBrutto);

        const vkFaktor = safeDiv(totalVkBrutto, totalBedienBrutto); 

        const deltaBreakEvenNetto = avgGesamtNetto - avgSollNetto;
        const deltaZielNetto = avgGesamtNetto - avgZielNetto;

        const istRuecklagenEur = avgGesamtNetto - avgSollNetto;
        const istRuecklagenPct = safeDiv(istRuecklagenEur, avgSollNetto);
        const zielRuecklagenEur = avgSollNetto * safeRuecklagenFaktor;
        const deltaRuecklagenEur = istRuecklagenEur - zielRuecklagenEur;

        const ausfallKostenVerlust = totalKrank * stammdaten.durchschnittlicherUmsatzProTag * (auslastungsVorgabe / 100);

        const arbeitstageProMonat = Number(stammdaten.arbeitstageProMonat) || 0;
        const teamSize = Math.max(1, Number(stammdaten.teamSize) || 1);
        const verfuegbareArbeitstage = count * arbeitstageProMonat * teamSize;
        const ausfallquote = safeDiv(totalKrank, verfuegbareArbeitstage);

        return {
            count, avgBedienBrutto, avgVkBrutto, avgGesamtBrutto, avgGesamtNetto, avgSollNetto, avgZielNetto, avgPrivatentnahme,
            vkFaktor, stdUmsatzIstBrutto, stdUmsatzSollBrutto, umsatzauslastung, totalKrank, totalUrlaub, ausfallKostenVerlust,
            ausfallquote, verfuegbareArbeitstage,
            deltaBreakEvenNetto, deltaZielNetto,
            istRuecklagenEur, istRuecklagenPct, zielRuecklagenEur, deltaRuecklagenEur, safeRuecklagenFaktor,
            avgKostenNetto, stdUmsatzSollBei75, teamEffizienzIst
        };
    }, [startMonth, endMonth, rawMonthsData, stammdaten.monatsStunden, stammdaten.durchschnittlicherUmsatzProTag, stammdaten.arbeitstageProMonat, stammdaten.teamSize, mwstFaktor, auslastungsVorgabe, ruecklagenZielFaktor, teamEffizienzIst]);
    
    // 💉 CHIRURGIE: Das Veto nutzt jetzt exakt die Store-Werte wie die Page!
    const rentStatus = getRentabilitaetStatus(gesamtUnternehmerlohn, storeUeberschussProzent);

    const effLvl = getEvaluationLevel(teamEffizienzIst, 'effizienz');
    const auslLvl = getEvaluationLevel(analysis.umsatzauslastung, 'auslastung');
    const vkLvl = getEvaluationLevel(analysis.vkFaktor, 'vk');

    const effLvlLabel = useMemo(() => {
        if (teamEffizienzIst >= 1.00) return 'Exzellent';
        if (teamEffizienzIst >= 0.95) return 'Sehr Gut';
        if (teamEffizienzIst >= 0.89) return 'Gut';
        if (teamEffizienzIst >= 0.72) return 'Akzeptabel';
        if (teamEffizienzIst >= 0.63) return 'Kritisch';
        return 'Defizitär';
    }, [teamEffizienzIst]);

    const handleStartKiPruefung = async () => {
        setIsAiLoading(true);
        setAiError(null);
        setShowAiAnalysis(false); 
        setAiDiagnostics([]);
        
        try {
            const functions = getFunctions(getApp(), 'europe-west1');
            const generateAi = httpsCallable(functions, 'generatePerformanceDiagnosticAi');
            
            const payloadMetrics = {
                ...analysis,
                uiStatus: {
                    rentabilitaetStatus: rentStatus.label,
                    effizienzStatus: effLvlLabel,
                    auslastungStatus: auslLvl.label,
                    vkStatus: vkLvl.label
                }
            };

            const res = await generateAi({ 
                level: 'salon',
                targetName: title || 'Gesamtunternehmen',
                metrics: payloadMetrics
            });
            
            const payload = (res.data as any)?.diagnostics;
            if (payload && Array.isArray(payload)) {
                setAiDiagnostics(payload);
                setShowAiAnalysis(true);
            } else {
                throw new Error("Das AI-Modell hat keine gültigen Diagnose-Daten zurückgeliefert.");
            }
        } catch (error: any) {
            console.error("KI-Prüfung konnte nicht durchgeführt werden:", error);
            setAiError(error?.message || "Unbekannter Systemfehler bei der Server-Kommunikation.");
        } finally {
            setIsAiLoading(false);
        }
    };

    const getMarkerPosition = () => {
        const be = analysis.avgSollNetto;
        const ziel = analysis.avgZielNetto;
        const ist = analysis.avgGesamtNetto;

        if (ist <= be) {
            if (be === 0) return 0;
            return (ist / be) * 33.33;
        } else if (ist <= ziel) {
            const range = ziel - be;
            if (range <= 0) return 33.33;
            return 33.33 + ((ist - be) / range) * 33.33;
        } else {
            const range = ziel * 0.2; 
            if (range <= 0) return 66.66;
            const overflow = Math.min(ist - ziel, range); 
            return 66.66 + ((overflow / range) * 33.33);
        }
    };

    let ruecklagenColor = 'text-red-700 dark:text-red-400 led-red print:text-red-600';
    let ruecklagenSubColor = 'text-red-500/80 print:text-red-600';

    if (analysis.deltaRuecklagenEur >= 0) {
        ruecklagenColor = 'text-emerald-700 dark:text-emerald-400 led-emerald print:text-emerald-600';
        ruecklagenSubColor = 'text-emerald-500/80 print:text-emerald-600';
    } else if (analysis.istRuecklagenEur >= 0) {
        ruecklagenColor = 'text-amber-700 dark:text-amber-400 led-amber print:text-yellow-600';
        ruecklagenSubColor = 'text-amber-500/80 print:text-yellow-600';
    }

    if (!isOpen || !mounted) return null;

    return createPortal(
        <div className="print-modal-wrapper fixed inset-0 z-[2147483647] flex justify-center items-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 sm:p-8 font-sans selection:bg-phantom-gold selection:text-black">
            <DashboardStyles />
            
            <style>{`
                @media print {
                    body > *:not(.print-modal-wrapper) { display: none !important; }
                    html, body { height: auto !important; overflow: visible !important; background: white !important; margin: 0 !important; padding: 0 !important; }
                    .print-modal-wrapper { position: relative !important; display: block !important; height: auto !important; overflow: visible !important; background: white !important; padding: 0 !important; margin: 0 !important; }
                    .print-modal-inner { border: none !important; box-shadow: none !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; background: white !important; color: black !important; }
                    .print-hide { display: none !important; }
                    .print-scroll-area { overflow: visible !important; height: auto !important; max-height: none !important; }
                    table { border-collapse: collapse; width: 100%; }
                    th, td { border-bottom: 1px solid #cbd5e1 !important; padding: 12px 8px !important; }
                    tr { page-break-inside: avoid; }
                    textarea { resize: none !important; border: none !important; padding: 0 !important; background: transparent !important; color: black !important; outline: none !important; }
                }
            `}</style>

            <div className="print-modal-inner relative carbon-panel carbon-weave-bg border border-pe-border-strong rounded-2xl w-full max-w-[1200px] max-h-[95vh] shadow-2xl flex flex-col text-pe-text dark:text-slate-200">
                
                <div className="flex-shrink-0 z-50 p-5 bg-pe-sunken border-b border-pe-border-strong flex flex-wrap justify-between items-center gap-6 print-hide rounded-t-2xl shadow-md">
                    <div className="flex items-center gap-3">
                        <Briefcase className="text-phantom-gold w-6 h-6" />
                        <div>
                            <h3 className="font-black tracking-widest uppercase text-lg text-pe-text leading-tight">Salon-Gesundheits-Zeugnis</h3>
                            {!isHJ2Freigegeben && <span className="text-[10px] text-orange-400 font-bold uppercase tracking-widest">2. Halbjahr gesperrt</span>}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3 bg-pe-input dark:bg-black px-4 py-2 rounded-xl border border-pe-border-strong shadow-inner">
                        <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider">Auswertung von:</span>
                        <select value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))} className="bg-pe-card border border-pe-border-strong dark:border-[#444] rounded p-1.5 text-pe-text text-xs font-bold uppercase tracking-widest focus:border-phantom-gold outline-none cursor-pointer">
                            {validMonths.map((idx) => <option key={`start-${idx}`} value={idx} disabled={idx > endMonth}>{monateNamen[idx]}</option>)}
                        </select>
                        <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider">bis:</span>
                        <select value={endMonth} onChange={(e) => setEndMonth(Number(e.target.value))} className="bg-pe-card border border-pe-border-strong dark:border-[#444] rounded p-1.5 text-pe-text text-xs font-bold uppercase tracking-widest focus:border-phantom-gold outline-none cursor-pointer">
                            {validMonths.map((idx) => <option key={`end-${idx}`} value={idx} disabled={idx < startMonth}>{monateNamen[idx]}</option>)}
                        </select>
                    </div>

                    <div className="flex items-center gap-3">
                        {!showAiAnalysis && (
                            <button 
                                onClick={handleStartKiPruefung}
                                disabled={isAiLoading}
                                className={`flex items-center gap-2 metal-badge bg-pe-card dark:bg-black hover:bg-pe-hover dark:hover:bg-[#111] border-pe-border-strong text-phantom-gold font-bold uppercase tracking-widest text-[10px] px-4 py-3 rounded-xl transition-all shadow-md ${isAiLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <Bot className={`w-4 h-4 ${isAiLoading ? 'animate-pulse' : ''}`} /> {isAiLoading ? 'Verbindung wird aufgebaut...' : 'KI-Prüfung starten'}
                            </button>
                        )}
                        <button onClick={() => window.print()} className="flex items-center gap-2 metal-badge hover:bg-pe-card text-pe-text font-bold text-[10px] px-5 py-3 rounded-xl transition-all shadow-md uppercase tracking-widest border border-pe-border-strong">
                            <Printer className="w-4 h-4" /> Ausdrucken
                        </button>
                        <button onClick={onClose} className="p-2.5 bg-pe-card dark:bg-black hover:bg-pe-hover dark:hover:bg-[#111] border border-pe-border-strong text-pe-muted hover:text-pe-text rounded-full transition-colors shadow-inner">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto print-scroll-area carbon-content p-6 md:p-10 space-y-8">
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-pe-card dark:bg-[#1c1c1e] border border-pe-border-strong shadow-xl p-5 rounded-xl print:bg-white print:border-slate-300 print:text-black">
                        <div>
                            <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider block mb-1">Salon-Arbeitszeit</span>
                            <span className="font-mono-numbers text-xl font-black text-pe-text">{formatNumber(stammdaten.monatsStunden, 1)} <span className="text-xs font-sans text-pe-muted">Std/Mo</span></span>
                        </div>
                        <div>
                            <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider block mb-1">Unternehmerlohn (Mtl.)</span>
                            <span className="font-mono-numbers text-xl font-black text-amber-700 dark:text-amber-400 led-amber print:text-black">{formatCurrency(analysis.avgPrivatentnahme, currencyCode)}</span>
                        </div>
                        <div>
                            <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider block mb-1">Break-Even (Kosten)</span>
                            <span className="font-mono-numbers text-xl font-black text-pe-text">{formatCurrency(analysis.avgSollNetto, currencyCode)} <span className="text-xs font-sans text-pe-muted">Netto</span></span>
                            <span className="block text-[10px] font-mono-numbers font-bold text-pe-muted mt-1 print:text-slate-500">Brutto: {formatCurrency(analysis.avgSollNetto * mwstFaktor, currencyCode)}</span>
                        </div>
                        <div>
                            <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider block mb-1">Ziel-Umsatz (+{formatPercent(ruecklagenZielFaktor, 0)})</span>
                            <span className="font-mono-numbers text-xl font-black text-phantom-gold led-gold print:text-black">{formatCurrency(analysis.avgZielNetto, currencyCode)} <span className="text-xs font-sans text-pe-muted">Netto</span></span>
                        </div>
                    </div>

                    {showAiAnalysis && !aiError && aiDiagnostics.length > 0 && (
                        <div className="relative border border-phantom-gold/30 rounded-2xl bg-pe-card dark:bg-black shadow-[0_0_30px_rgba(230,194,153,0.1)] overflow-hidden print:bg-white print:border-slate-300 animate-in fade-in zoom-in-95 duration-300">
                            <div className="p-5 flex items-center gap-3 border-b border-pe-border-strong print:border-slate-300 print:bg-slate-50 bg-pe-tile-header">
                                <span className="text-xl">🤖</span>
                                <h3 className="font-black uppercase tracking-widest text-sm text-phantom-gold print:text-black">KI-Diagnose Report (Salon)</h3>
                            </div>

                            <div className="p-6 space-y-4">
                                <div className="text-sm text-pe-muted dark:text-slate-300 mb-6 print:text-black leading-relaxed">
                                    Die aggregierte Analyse des Salons <strong className="text-pe-text">{title || 'Gesamtunternehmen'}</strong> offenbart die globale Performance des Teams. Bitte prüfe die folgenden Kausalitäten für die strategische Ausrichtung:
                                </div>

                                {aiDiagnostics.map((diag, i) => (
                                    <KICard 
                                        key={i}
                                        type={diag.type} 
                                        title={diag.title}
                                        icon={diag.icon || "💡"}
                                    >
                                        {diag.text}
                                    </KICard>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TOP-GRID (3 Elemente) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                        
                        <CustomTooltip text="Zeigt auf einen Blick, ob der Salon mindestens kostendeckend arbeitet oder sogar bereits sein anvisiertes Rücklagenziel erreicht.">
                            <div className="h-full">
                                <OriginalLeistungsFeedbackBox
                                    ruecklagenZielFaktor={ruecklagenZielFaktor}
                                    istRuecklagenPct={analysis.istRuecklagenPct}
                                    gewinn={analysis.avgGesamtNetto - analysis.avgSollNetto + analysis.avgPrivatentnahme}
                                    hasData={analysis.avgGesamtNetto !== 0 || analysis.avgSollNetto !== 0}
                                />
                            </div>
                        </CustomTooltip>

                        {/* 💉 CHIRURGIE: Reale Rentabilität - Exakt gemappt und erweitert um die Inhaber-Ertrag Box */}
                        <CustomTooltip text="Zeigt die echte Unternehmensgesundheit. Hiermit müssen nicht nur Gewinne, sondern auch Unternehmerlohn, Rücklagen und Steuern finanziert werden.">
                            <div className={`bg-pe-card dark:bg-[#1c1c1e] border ${rentStatus.border} flex flex-col rounded-xl shadow-xl print:bg-white print:border-slate-300 h-full overflow-hidden`}>
                                <div className="p-5 flex flex-col items-center text-center flex-grow justify-center">
                                    <div className="inline-flex items-center gap-2">
                                        <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">REALE RENTABILITÄT</span>
                                        <Info className="w-3 h-3 text-pe-muted" />
                                    </div>
                                    <div className={`text-4xl font-black font-mono-numbers mt-2 tabular-nums ${rentStatus.color}`}>
                                        {formatPercent(storeRentabilitaetIst, 1)}
                                    </div>
                                    <span className={`text-[10px] uppercase font-bold mt-2 tracking-widest ${rentStatus.color}`}>{rentStatus.label}</span>
                                </div>
                                <div className={`px-4 py-3 bg-pe-sunken print:bg-white border-t ${rentStatus.border}`}>
                                    <p className={`text-[10px] font-sans text-center ${rentStatus.color.replace(/led-[a-z]+/, '').trim()}`}>
                                        {rentStatus.text}
                                    </p>
                                </div>
                                <div className="px-4 py-3 bg-pe-tile-header print:bg-slate-50 border-t border-pe-border-strong flex items-start gap-3 print:border-slate-200">
                                    <div className="text-blue-500 mt-0.5 shrink-0">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="16" x2="12" y2="12"></line>
                                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                        </svg>
                                    </div>
                                    <div className="flex flex-col space-y-1 text-left">
                                        <p className="text-[10px] text-pe-muted italic leading-relaxed print:text-slate-600">
                                            Realer Inhaber-Ertrag: <span className="font-bold font-mono-numbers text-phantom-gold led-gold print:text-black">{formatCurrency(inhaberErtragIst, currencyCode, 0)} / Monat</span> (vor Steuern).
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CustomTooltip>

                        <CustomTooltip text="Zeigt, wie viel von deinem Rücklagenziel tatsächlich in der Kasse landet. Positiv bedeutet, das Polster wächst.">
                            <div className="bg-pe-card dark:bg-[#1c1c1e] border border-pe-border-strong p-5 rounded-xl flex flex-col shadow-xl print:bg-white print:border-slate-300 print:text-black h-full">
                                <div className="inline-flex items-center gap-2">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">GEBILDETE RÜCKLAGEN (MTL.)</span>
                                    <Info className="w-3 h-3 text-pe-muted" />
                                </div>
                                <div className="flex flex-wrap items-baseline gap-x-2 mt-2">
                                    <span className={`text-3xl font-black font-mono-numbers tabular-nums whitespace-nowrap ${ruecklagenColor}`}>
                                        {analysis.istRuecklagenEur > 0 ? '+' : ''}{formatCurrency(analysis.istRuecklagenEur, currencyCode)}
                                    </span>
                                    <span className={`text-sm font-bold font-sans whitespace-nowrap ${ruecklagenSubColor}`}>
                                        ({formatPercent(analysis.istRuecklagenPct, 1)})
                                    </span>
                                </div>
                                
                                <div className="mt-auto pt-3 border-t border-pe-border-strong print:border-slate-200">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-600">Ziel ({formatPercent(ruecklagenZielFaktor, 0)}):</span>
                                        <span className="text-[11px] font-mono-numbers text-pe-muted font-bold print:text-slate-700">{formatCurrency(analysis.zielRuecklagenEur, currencyCode)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-600">Differenz:</span>
                                        <span className={`text-[11px] font-mono-numbers font-black ${analysis.deltaRuecklagenEur >= 0 ? 'text-emerald-400 print:text-emerald-700' : 'text-red-400 print:text-red-700'}`}>
                                            {analysis.deltaRuecklagenEur > 0 ? '+' : ''}{formatCurrency(analysis.deltaRuecklagenEur, currencyCode)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </CustomTooltip>

                    </div>

                    {/* RENTABILITÄTS-SKALA (Immer sichtbar) */}
                    <div className="bg-pe-sunken border border-pe-border-strong p-5 rounded-xl shadow-inner print:bg-white print:border-slate-300">
                        <div className="flex justify-between items-end mb-5">
                            <h4 className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-600">Rentabilitäts-Skala (Netto)</h4>
                            <div className="text-right">
                                <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Erreichter Ø Umsatz</span>
                                <div className="bg-orange-100/80 dark:bg-orange-950/20 border border-orange-300 dark:border-orange-500/30 px-3 py-1.5 rounded-lg text-orange-950 dark:text-orange-400 font-mono-numbers font-black text-xl mt-1 print:bg-transparent print:text-black print:border-slate-300 shadow-inner">
                                    {formatCurrency(analysis.avgGesamtNetto, currencyCode)}
                                </div>
                            </div>
                        </div>
                        
                        <div className="relative h-10 bg-pe-sunken dark:bg-black rounded-lg overflow-hidden flex border border-pe-border dark:border-[#222] shadow-inner print:border print:border-slate-300">
                            <div className="h-full w-1/3 bg-red-100 dark:bg-red-950/40 border-r border-pe-border-strong flex items-center px-4 print:bg-red-100">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-red-700 dark:text-red-500 truncate print:text-red-700">Unter Deckung</span>
                            </div>
                            <div className="h-full w-1/3 bg-amber-100 dark:bg-amber-950/40 border-r border-pe-border-strong flex items-center px-4 print:bg-yellow-100">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-500 truncate print:text-yellow-700">Zone Deckung</span>
                            </div>
                            <div className="h-full w-1/3 bg-emerald-100 dark:bg-emerald-950/40 flex items-center px-4 print:bg-emerald-100">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-400 truncate print:text-emerald-700">Zielbereich Profitabel</span>
                            </div>
                            
                            <div 
                                className="absolute top-0 bottom-0 w-2 bg-white border-2 border-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.8)] z-10 print:border-black transition-all duration-500"
                                style={{ left: `calc(${getMarkerPosition()}% - 4px)` }}
                            />
                        </div>
                    </div>

                    {/* BOTTOM-GRID: Vitals (3 oder 2 Elemente, abhängig von showTeamData) */}
                    <div className={`grid grid-cols-2 ${showTeamData ? 'md:grid-cols-3' : 'md:grid-cols-2 w-full max-w-2xl mx-auto'} gap-5 items-stretch`}>
                        
                        {showTeamData && (
                            <CustomTooltip text="Bewertet die Arbeitsdichte und den Fleiß im Salon unabhängig vom Ziel. Zeigt, wie gut die reine Anwesenheitszeit des Teams in echten Umsatz verwandelt wird.">
                                <div className={`bg-pe-card dark:bg-[#1c1c1e] border ${effLvl.borderClass} p-5 rounded-xl text-center shadow-xl flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full`}>
                                    <div className="inline-flex items-center gap-2 mb-2">
                                        <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Ø Team-Effizienz</span>
                                        <Info className="w-3 h-3 text-pe-muted" />
                                    </div>
                                    <div className={`text-4xl font-black font-mono-numbers mb-3 tabular-nums ${effLvl.textClass} ${effLvl.shadowClass} print:text-black print:drop-shadow-none`}>
                                        {formatPercent(teamEffizienzIst, 1)}
                                    </div>
                                    <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${effLvl.badgeBg} ${effLvl.textClass} print:bg-slate-100 print:border-slate-400`}>
                                        {effLvlLabel}
                                    </span>
                                    <span className="text-[9px] text-slate-600 font-bold uppercase mt-2 tracking-widest print:text-slate-400">(Ref: 100%)</span>
                                </div>
                            </CustomTooltip>
                        )}

                        <CustomTooltip text="Misst die Lukrativität des Unternehmens. Zeigt, wie gut das Team seine Zeit nutzt, um die kalkulierten Kostendeckungs- und Rücklagenziele (SOLL) zu erreichen.">
                            <div className={`bg-pe-card dark:bg-[#1c1c1e] border ${auslLvl.borderClass} p-5 rounded-xl text-center shadow-xl flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full`}>
                                <div className="inline-flex items-center gap-2 mb-2">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Ø Salon-Umsatzauslastung</span>
                                    <Info className="w-3 h-3 text-pe-muted" />
                                </div>
                                <div className={`text-4xl font-black font-mono-numbers mb-3 tabular-nums ${auslLvl.textClass} ${auslLvl.shadowClass} print:text-black print:drop-shadow-none`}>
                                    {formatPercent(analysis.umsatzauslastung, 1)}
                                </div>
                                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${auslLvl.badgeBg} ${auslLvl.textClass} print:bg-slate-100 print:border-slate-400`}>
                                    {auslLvl.label}
                                </span>
                                <span className="text-[9px] text-slate-600 font-bold uppercase mt-2 tracking-widest print:text-slate-400">(Ziel: {auslastungsVorgabe}%)</span>
                            </div>
                        </CustomTooltip>

                        <CustomTooltip text="Zeigt den reinen Produktverkauf (VK) des Salons im prozentualen Verhältnis zur erbrachten Dienstleistung.">
                            <div className={`bg-pe-card dark:bg-[#1c1c1e] border ${vkLvl.borderClass} p-5 rounded-xl text-center shadow-xl flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full`}>
                                <div className="inline-flex items-center gap-2 mb-2">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Salon-Verkaufsfaktor</span>
                                    <Info className="w-3 h-3 text-pe-muted" />
                                </div>
                                <div className={`text-4xl font-black font-mono-numbers mb-3 tabular-nums ${vkLvl.textClass} ${vkLvl.shadowClass} print:text-black print:drop-shadow-none`}>
                                    {formatPercent(analysis.vkFaktor, 1)}
                                </div>
                                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${vkLvl.badgeBg} ${vkLvl.textClass} print:bg-slate-100 print:border-slate-400`}>
                                    {vkLvl.label}
                                </span>
                                <span className="text-[9px] text-slate-600 font-bold uppercase mt-2 tracking-widest print:text-slate-400">(Ziel: 10.0%)</span>
                            </div>
                        </CustomTooltip>

                    </div>

                    {/* ABSENZEN & RISIKO — nebeneinander wie im Mitarbeiter-Modal */}
                    {showTeamData && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
                        <CustomTooltip text="Dokumentierte Urlaubstage des Teams im gewählten Zeitraum.">
                            <div className="bg-pe-card dark:bg-[#1c1c1e] border border-pe-border-strong p-5 rounded-xl text-center shadow-xl flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full">
                                <div className="inline-flex items-center gap-2 mb-2">
                                    <span className="text-[10px] font-bold text-amber-500 uppercase tracking-widest print:text-yellow-600">Urlaubstage</span>
                                </div>
                                <div className="text-4xl font-black font-mono-numbers mb-3 tabular-nums text-amber-700 dark:text-amber-400 led-amber print:text-yellow-700 print:drop-shadow-none">
                                    {formatNumber(analysis.totalUrlaub, 0)} <span className="text-sm font-sans font-bold">Tage</span>
                                </div>
                                <span className="text-[9px] text-pe-muted mt-2 font-bold uppercase tracking-widest print:text-yellow-600">Geplanter Urlaub</span>
                            </div>
                        </CustomTooltip>

                        <CustomTooltip text="Dokumentierte Krankheitstage des Teams, Ausfallquote (Krankheitstage ÷ verfügbare Team-Arbeitstage im Zeitraum) und Zeitraum.">
                            <div className={`bg-pe-card dark:bg-[#1c1c1e] border ${analysis.totalKrank > 0 ? 'border-red-500/30' : 'border-pe-border-strong'} p-5 rounded-xl text-center shadow-xl flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full`}>
                                <div className="inline-flex items-center gap-2 mb-2">
                                    <Stethoscope className={`w-4 h-4 ${analysis.totalKrank > 0 ? 'text-red-600 dark:text-red-500 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`} />
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${analysis.totalKrank > 0 ? 'text-red-600 dark:text-red-500 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`}>Krankheitstage</span>
                                </div>
                                <div className={`text-4xl font-black font-mono-numbers mb-2 tabular-nums print:drop-shadow-none ${analysis.totalKrank > 0 ? 'text-red-700 dark:text-red-400 led-red print:text-red-700' : 'text-pe-muted dark:text-slate-300 print:text-slate-600'}`}>
                                    {formatNumber(analysis.totalKrank, 0)} <span className="text-sm font-sans font-bold">Tage</span>
                                </div>
                                <div className={`text-lg font-black font-mono-numbers tabular-nums ${analysis.totalKrank > 0 ? 'text-red-500 dark:text-red-400 print:text-red-700' : 'text-pe-muted'}`}>
                                    {formatPercent(analysis.ausfallquote, 1)}
                                </div>
                                <span className={`text-[9px] font-bold uppercase mt-1 tracking-widest ${analysis.totalKrank > 0 ? 'text-red-700/80 dark:text-red-500/70 print:text-red-500' : 'text-slate-600 print:text-slate-400'}`}>
                                    Ausfallquote
                                    {analysis.verfuegbareArbeitstage > 0
                                        ? ` · ${formatNumber(analysis.verfuegbareArbeitstage, 0)} AT-Tage`
                                        : ''}
                                </span>
                            </div>
                        </CustomTooltip>

                        <CustomTooltip text="Berechnet die finanziellen Einbußen im Gesamtumsatz des Salons, basierend auf den dokumentierten Krankheitstagen des Teams.">
                            <div className={`bg-pe-card dark:bg-[#1c1c1e] border ${analysis.totalKrank > 0 ? 'border-red-500/30' : 'border-pe-border-strong'} p-5 rounded-xl flex flex-col justify-between shadow-xl print:bg-white print:border-slate-300 print:text-black h-full`}>
                                <div className="inline-flex items-center gap-2">
                                    <span className={`block text-[10px] font-bold tracking-widest uppercase ${analysis.totalKrank > 0 ? 'text-red-600 dark:text-red-500 print:text-red-500' : 'text-pe-muted print:text-slate-500'}`}>Ø UMSATZVERLUST (KRANKHEIT)</span>
                                    <Info className={`w-3 h-3 ${analysis.totalKrank > 0 ? 'text-red-500/80' : 'text-pe-muted'}`} />
                                </div>
                                <div className="flex flex-wrap items-baseline justify-center gap-x-2 mt-2">
                                    <span className={`text-3xl font-black font-mono-numbers tabular-nums whitespace-nowrap print:drop-shadow-none ${analysis.totalKrank > 0 ? 'text-red-700 dark:text-red-400 led-red print:text-red-600' : 'text-pe-muted dark:text-slate-300 print:text-slate-600'}`}>
                                        {analysis.totalKrank > 0 ? `-${formatCurrency(analysis.ausfallKostenVerlust, currencyCode, 0)}` : formatCurrency(0, currencyCode, 0)}
                                    </span>
                                </div>
                                <span className={`text-[10px] uppercase font-bold mt-auto pt-3 tracking-widest text-center ${analysis.totalKrank > 0 ? 'text-red-700/80 dark:text-red-500/70 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`}>Kalkulatorischer Ausfall</span>
                            </div>
                        </CustomTooltip>
                    </div>
                    )}

                    {/* CEO FINANZ-MATRIX (Immer sichtbar) */}
                    <CustomTooltip text="Eine Gegenüberstellung der erbrachten Leistung (IST) mit den kalkulierten Zielwerten (SOLL) für den gesamten Salon.">
                        <div className="border border-pe-border-strong rounded-xl overflow-hidden shadow-2xl print:border-slate-300 print:rounded-none">
                            <table className="w-full text-base font-sans bg-transparent">
                                <thead className="bg-pe-tile-header text-pe-muted border-b-2 border-pe-border-strong print:bg-slate-100 print:text-black">
                                    <tr>
                                        <th className="p-4 text-left font-black uppercase text-[10px] tracking-widest">CEO Finanz-Matrix (Mtl. Ø)</th>
                                        <th className="p-4 text-right font-black uppercase text-[10px] tracking-widest">Dienstleistung</th>
                                        <th className="p-4 text-right font-black uppercase text-[10px] tracking-widest">Verkauf</th>
                                        <th className="p-4 text-right font-black uppercase text-[10px] tracking-widest">Gesamtertrag (Brutto)</th>
                                        <th className="p-4 text-right font-black uppercase text-[10px] tracking-widest">Reales Netto</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#333] font-bold print:divide-slate-200 print:text-black">
                                    <tr className="hover:bg-pe-card transition-colors print:bg-white bg-pe-sunken dark:bg-black">
                                        <td className="p-4 text-pe-text text-sm uppercase tracking-widest print:text-black">Erreichte Leistung (IST)</td>
                                        <td className="p-4 text-right font-mono-numbers tabular-nums text-pe-muted dark:text-slate-300">{formatCurrency(analysis.avgBedienBrutto, currencyCode)}</td>
                                        <td className="p-4 text-right font-mono-numbers tabular-nums text-pe-muted dark:text-slate-300">{formatCurrency(analysis.avgVkBrutto, currencyCode)}</td>
                                        <td className="p-4 text-right font-mono-numbers tabular-nums font-black text-orange-700 dark:text-orange-400 led-orange print:text-black print:drop-shadow-none">{formatCurrency(analysis.avgGesamtBrutto, currencyCode)}</td>
                                        <td className="p-4 text-right font-mono-numbers tabular-nums font-black text-emerald-700 dark:text-emerald-400 led-emerald print:text-black print:drop-shadow-none">{formatCurrency(analysis.avgGesamtNetto, currencyCode)}</td>
                                    </tr>
                                    <tr className="border-t-2 border-pe-border-strong bg-pe-sunken print:bg-white print:border-slate-300">
                                        <td className="p-4 text-pe-muted uppercase text-xs font-bold tracking-widest print:text-slate-700">Reine Mindest-Kostendeckung</td>
                                        <td colSpan={2} className="p-4 text-right text-[10px] font-bold uppercase tracking-widest text-slate-600 print:text-slate-500">Lohn- & Fixkosten (Break-Even):</td>
                                        <td className="p-4 text-right font-mono-numbers tabular-nums text-pe-muted print:text-slate-900">{formatCurrency(analysis.avgSollNetto * mwstFaktor, currencyCode)}</td>
                                        <td className="p-4 text-right font-mono-numbers tabular-nums text-pe-muted print:text-slate-900">{formatCurrency(analysis.avgSollNetto, currencyCode)}</td>
                                    </tr>
                                    
                                    <tr className="bg-pe-tile-header print:bg-slate-50">
                                        <td className={`p-4 font-bold uppercase tracking-widest text-xs flex items-center gap-2 ${analysis.deltaBreakEvenNetto >= 0 ? 'text-pe-muted print:text-slate-600' : 'text-red-600 dark:text-red-500 print:text-red-600'}`}>
                                            {analysis.deltaBreakEvenNetto >= 0 ? 'Deckungs-Überschuss' : 'Deckungs-Fehlbetrag'}
                                        </td>
                                        <td colSpan={2} className="p-4 text-right text-[10px] font-bold uppercase tracking-widest text-slate-600 print:text-slate-500">Differenz zum Break-Even:</td>
                                        <td colSpan={2} className={`p-4 text-right font-mono-numbers tabular-nums font-black text-lg ${analysis.deltaBreakEvenNetto >= 0 ? 'text-pe-muted dark:text-slate-300 print:text-slate-600' : 'text-red-700 dark:text-red-400 led-red print:drop-shadow-none print:text-red-700'}`}>
                                            {analysis.deltaBreakEvenNetto > 0 ? '+' : ''}{formatCurrency(analysis.deltaBreakEvenNetto, currencyCode, 0)}
                                        </td>
                                    </tr>

                                    <tr className="bg-pe-card print:bg-slate-50 border-t-2 border-pe-border-strong print:border-slate-300">
                                        <td className="p-4 text-phantom-gold uppercase font-bold text-xs tracking-widest print:text-black">Strategisches Ziel (SOLL 100%)</td>
                                        <td colSpan={2} className="p-4 text-right text-[10px] font-bold uppercase tracking-widest text-slate-600 print:text-slate-500">Break-Even + Rücklagen ({formatPercent(ruecklagenZielFaktor, 0)}):</td>
                                        <td className="p-4 text-right font-mono-numbers tabular-nums text-phantom-gold print:text-slate-600">{formatCurrency(analysis.avgZielNetto * mwstFaktor, currencyCode)}</td>
                                        <td className="p-4 text-right font-mono-numbers tabular-nums text-phantom-gold print:text-slate-600">{formatCurrency(analysis.avgZielNetto, currencyCode)}</td>
                                    </tr>
                                    
                                    <tr className={`border-t-4 border-pe-border-strong dark:border-[#444] print:border-slate-400 ${analysis.deltaZielNetto >= 0 ? 'bg-emerald-100/60 dark:bg-emerald-950/20 print:bg-green-50/50' : 'bg-red-100/60 dark:bg-red-950/20 print:bg-red-50/50'}`}>
                                        <td className={`p-4 font-black text-xs uppercase tracking-widest flex items-center gap-2 ${analysis.deltaZielNetto >= 0 ? 'text-emerald-400 print:text-green-700' : 'text-red-400 print:text-red-700'}`}>
                                            {analysis.deltaZielNetto >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />} 
                                            {analysis.deltaZielNetto >= 0 ? 'Erwirtschafteter Überschuss (mtl.)' : 'Ø Netto-Rückstand (mtl.)'}
                                        </td>
                                        <td colSpan={2} className="p-4 text-right text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Differenz zum Ziel (100%):</td>
                                        <td colSpan={2} className={`p-4 text-right font-mono-numbers tabular-nums font-black text-2xl print:drop-shadow-none ${analysis.deltaZielNetto >= 0 ? 'text-emerald-700 dark:text-emerald-400 led-emerald print:text-green-700' : 'text-red-700 dark:text-red-400 led-red print:text-red-700'}`}>
                                            {analysis.deltaZielNetto > 0 ? '+' : ''}{formatCurrency(analysis.deltaZielNetto, currencyCode, 0)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </CustomTooltip>

                    {/* MASSNAHMEN UND UNTERSCHRIFT (Immer sichtbar) */}
                    <div className="pt-8 border-t-2 border-pe-border-strong space-y-4 print:border-slate-300 print:pt-8 print:break-inside-avoid">
                        <div>
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-phantom-gold mb-3 print:text-black">Strategische Maßnahmen (Marketing, Personal, Preisgestaltung):</h4>
                            <textarea 
                                value={vereinbarungText}
                                onChange={(e) => setVereinbarungText(e.target.value)}
                                placeholder="Klicke hier, um strategische Maßnahmen direkt einzutragen..."
                                rows={4}
                                className="w-full mt-2 bg-pe-input dark:bg-black border border-pe-border-strong dark:border-[#444] rounded-xl p-5 text-pe-muted dark:text-slate-300 font-sans leading-relaxed placeholder-stone-400 dark:placeholder-slate-600 focus:outline-none focus:border-phantom-gold focus:ring-1 focus:ring-phantom-gold resize-y shadow-inner print:bg-transparent print:border-none print:text-black print:p-0 print:h-auto print:resize-none"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-16 pt-16 text-center text-[10px] uppercase tracking-widest font-bold text-slate-600 print:text-black print:mt-12 w-1/2 mx-auto">
                            <div className="space-y-3">
                                <div className="border-b border-pe-border-strong dark:border-[#444] w-full h-8 print:border-black"></div>
                                <span>Datum & Unterschrift Saloninhaber/in</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>,
        document.body
    );
};

export default ExecutiveSummaryModalGesamt;